import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Clock, Pencil, Ban, RotateCcw, CalendarOff } from "lucide-react";
import { isBye, BYE_UPDATE, UNBYE_UPDATE } from "@/utils/bye";

import { CancelGameDialog } from "./cancel-game-dialog";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { Game } from "@/types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { LocationAutocomplete } from "./location-autocomplete";

interface EditGameDialogProps {
  game: Game;
  onSaved: () => void;
  children?: React.ReactNode;
}

export function EditGameDialog({ game, onSaved, children }: EditGameDialogProps) {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const isCancelled = (game as any).status === "cancelled";
  const gameIsBye = isBye(game as any);

  const toggleBye = async (makeBye: boolean) => {
    setSaving(true);
    try {
      // When turning a bye back into a real match, the opponent field usually
      // still reads "Bye" — leaving it would keep isBye() true forever.
      const typedOpponent = opponent.trim();
      const restoredOpponent = isBye({ opponent: typedOpponent })
        ? "TBC"
        : typedOpponent || "TBC";
      const { error } = await supabase
        .from("teamvote_games")
        .update(
          makeBye
            ? { ...BYE_UPDATE, opponent: "Bye" }
            : { ...UNBYE_UPDATE, opponent: restoredOpponent },
        )
        .eq("id", game.id);
      if (error) throw error;
      toast.success(
        makeBye ? "Marked as a bye — no RSVPs needed" : "Back to a normal match",
      );
      setOpen(false);
      onSaved();
    } catch (err) {
      console.error("Error updating bye state:", err);
      toast.error("Couldn't update this match");
    } finally {
      setSaving(false);
    }
  };


  const [date, setDate] = useState<Date>(new Date(game.date + "T12:00:00"));
  const [opponent, setOpponent] = useState(game.opponent);
  const [matchTime, setMatchTime] = useState(game.match_time || "");
  const [matchLocation, setMatchLocation] = useState(game.match_location || "");
  const [matchLat, setMatchLat] = useState<number | null>(game.match_lat ?? null);
  const [matchLng, setMatchLng] = useState<number | null>(game.match_lng ?? null);
  const [formattedAddress, setFormattedAddress] = useState<string | null>(null);
  const [matchField, setMatchField] = useState(game.match_field || "");
  const [notes, setNotes] = useState((game as any).notes || "");

  const dateChanged = format(date, "yyyy-MM-dd") !== game.date;
  const timeChanged = (matchTime.trim() || null) !== (game.match_time || null);
  const willResetRsvp = dateChanged || timeChanged;

  const resetForm = () => {
    setDate(new Date(game.date + "T12:00:00"));
    setOpponent(game.opponent);
    setMatchTime(game.match_time || "");
    setMatchLocation(game.match_location || "");
    setMatchLat(game.match_lat ?? null);
    setMatchLng(game.match_lng ?? null);
    setFormattedAddress(null);
    setMatchField(game.match_field || "");
    setNotes((game as any).notes || "");
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const updates: Record<string, unknown> = {
        date: format(date, "yyyy-MM-dd"),
        opponent: opponent.trim() || format(date, "MMM d, yyyy"),
        match_time: matchTime.trim() || null,
        match_location: matchLocation.trim() || null,
        match_lat: matchLat,
        match_lng: matchLng,
        match_field: matchField.trim() || null,
        notes: notes.trim() || null,
      };

      const { error } = await supabase
        .from("teamvote_games")
        .update(updates)
        .eq("id", game.id);

      if (error) throw error;

      if (willResetRsvp) {
        const { error: rsvpError } = await supabase
          .from("game_rsvps")
          .delete()
          .eq("game_id", game.id);

        if (rsvpError) {
          console.warn("Failed to reset RSVPs:", rsvpError);
        }
      }

      toast.success(
        willResetRsvp
          ? "Game updated — RSVPs have been reset"
          : "Game updated"
      );
      setOpen(false);
      onSaved();
    } catch (err) {
      console.error("Error updating game:", err);
      toast.error("Failed to update game");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (willResetRsvp) {
      setConfirmOpen(true);
    } else {
      doSave();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (v) resetForm();
        }}
      >
        <DialogTrigger asChild>
          {children ?? (
            <Button variant="outline" size="sm" className="gap-1">
              <Pencil className="h-4 w-4" />
              <span>Edit</span>
            </Button>
          )}
        </DialogTrigger>

        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Game</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* Status / cancellation */}
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {gameIsBye
                      ? "No game this round (bye)"
                      : isCancelled
                        ? "Match is cancelled"
                        : "Match is scheduled"}
                  </p>
                  {isCancelled && (game as any).cancellation_reason && (
                    <p className="text-xs text-muted-foreground truncate">
                      Reason: {(game as any).cancellation_reason}
                    </p>
                  )}
                  {gameIsBye && (
                    <p className="text-xs text-muted-foreground">
                      Shown on the schedule. Nobody is asked to RSVP.
                    </p>
                  )}
                  {!isCancelled && !gameIsBye && (
                    <p className="text-xs text-muted-foreground">
                      Use this if the game is washed out or called off.
                    </p>
                  )}
                </div>
                {!gameIsBye && (
                  <Button
                    type="button"
                    variant={isCancelled ? "outline" : "destructive"}
                    size="sm"
                    className="gap-1 shrink-0"
                    onClick={() => {
                      setOpen(false);
                      setTimeout(() => setCancelOpen(true), 50);
                    }}
                  >
                    {isCancelled ? (
                      <>
                        <RotateCcw className="h-4 w-4" /> Restore
                      </>
                    ) : (
                      <>
                        <Ban className="h-4 w-4" /> Cancel match
                      </>
                    )}
                  </Button>
                )}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 mt-3 w-full"
                disabled={saving}
                onClick={() => toggleBye(!gameIsBye)}
              >
                <CalendarOff className="h-4 w-4" />
                {gameIsBye ? "This is a real match" : "No game — mark as bye"}
              </Button>
            </div>


            {/* Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">Game Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-start font-normal")}
                  >
                    <CalendarIcon className="me-2 h-4 w-4" />
                    {format(date, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => d && setDate(d)}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Kick-off time */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">Kick-off Time</label>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Input
                  type="time"
                  value={matchTime}
                  onChange={(e) => setMatchTime(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">Location</label>
              <LocationAutocomplete
                value={matchLocation}
                lat={matchLat}
                lng={matchLng}
                formattedAddress={formattedAddress}
                onChange={({ address, lat, lng, formattedAddress: fa }) => {
                  setMatchLocation(address);
                  setMatchLat(lat);
                  setMatchLng(lng);
                  setFormattedAddress(fa ?? null);
                }}
              />
            </div>

            {/* Field / Pitch */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">Field / Pitch</label>
              <Input
                value={matchField}
                onChange={(e) => setMatchField(e.target.value)}
                placeholder="e.g. Field 1, Pitch 3"
                className="w-full"
              />
            </div>

            {/* Opponent */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">Opponent</label>
              <Input
                value={opponent}
                onChange={(e) => setOpponent(e.target.value)}
                placeholder="Opponent team name"
                className="w-full"
              />
            </div>

            {/* Notes for parents */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Notes for parents <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 280))}
                placeholder="e.g. Parking is hard — arrive early. Wear away kit. Sam to bring fruit."
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
                maxLength={280}
              />
              <p className="text-xs text-muted-foreground">
                Shown as a friendly post-it on the RSVP page and in calendar invites. {280 - notes.length} characters left.
              </p>
            </div>

            {willResetRsvp && (
              <p className="text-sm text-destructive">
                ⚠ Changing date or time will reset all RSVPs for this game.
              </p>
            )}

            <Button
              className="w-full"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset RSVPs?</AlertDialogTitle>
            <AlertDialogDescription>
              You've changed the date or kick-off time. All existing RSVPs will
              be cleared so parents can re-confirm. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSave} disabled={saving}>
              {saving ? "Saving…" : "Update & Reset RSVPs"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CancelGameDialog
        game={game}
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onChanged={() => onSaved()}
      />
    </>
  );
}
